/**
 * FlyoutPortal — portal-based flyout wrapper for anchored popovers.
 *
 * Renders children outside clipping ancestors and keeps the flyout anchored to
 * its trigger while the window or any nested scroll container moves.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useOnViewportChange } from "reactflow";
import { useBlockingOverlay } from "../../../../hooks/canvasShortcutGuard";
import { createDebug } from "../../../../utils/debugLog";
import { useClickOutside } from "./useClickOutside";

const portalLog = createDebug("dw:config-flyout:portal");
const VIEWPORT_GUTTER = 8;
const TRIGGER_GAP = 8;

export type FlyoutPlacement = "above" | "below";

export interface FlyoutPosition {
  top: number;
  left: number;
  placement: FlyoutPlacement;
  flipped: boolean;
  horizontalClamped: boolean;
  verticalClamped: boolean;
  width: number;
  height: number;
  viewportWidth: number;
  viewportHeight: number;
}

interface Position {
  top: number;
  left: number;
  placement: FlyoutPlacement;
  flipped: boolean;
  horizontalClamped: boolean;
  verticalClamped: boolean;
}

export interface FlyoutPortalProps {
  triggerRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  surfaceClassName?: string;
  minWidth?: number;
  preferredPlacement?: FlyoutPlacement;
  closeWhenTriggerLeavesViewport?: boolean;
  closeOnCanvasViewportChange?: boolean;
  onEscape?: () => void;
  onPositionChange?: (position: FlyoutPosition) => void;
  portalHost?: Element | DocumentFragment | null;
  testId?: string;
  /**
   * Stacking order. Defaults to the canvas value.
   *
   * The portal mounts into `document.body`, so it is a SIBLING of every other
   * body-level portal and shares their stacking context — it does not inherit
   * the z-index of whatever opened it. A flyout opened from inside `ui/Modal`
   * (`z-[1100]`) therefore renders BEHIND the modal at the canvas default and
   * is invisible while still being in the DOM.
   *
   * Opt-in: omitted leaves the `z-[150]` class exactly as it was, so canvas
   * callers are unaffected. When given, it is applied as an inline style so it
   * cannot lose to Tailwind's utility ordering.
   */
  zIndex?: number;
}

function computePosition(
  triggerEl: HTMLElement,
  portalEl: HTMLElement,
  minWidth: number,
  preferredPlacement: FlyoutPlacement,
): Position {
  const rect = triggerEl.getBoundingClientRect();
  const portalHeight = portalEl.offsetHeight;
  const portalWidth = Math.max(portalEl.offsetWidth, minWidth);
  const aboveTop = rect.top - portalHeight - TRIGGER_GAP;
  const belowTop = rect.bottom + TRIGGER_GAP;
  const viewportBottom = window.innerHeight - VIEWPORT_GUTTER;

  let placement = preferredPlacement;
  let top = placement === "above" ? aboveTop : belowTop;
  let flipped = false;

  if (
    placement === "above" &&
    top < VIEWPORT_GUTTER &&
    belowTop + portalHeight <= viewportBottom
  ) {
    placement = "below";
    top = belowTop;
    flipped = true;
  } else if (
    placement === "below" &&
    top + portalHeight > viewportBottom &&
    aboveTop >= VIEWPORT_GUTTER
  ) {
    placement = "above";
    top = aboveTop;
    flipped = true;
  }

  let verticalClamped = false;
  if (top + portalHeight > viewportBottom) {
    top = Math.max(VIEWPORT_GUTTER, viewportBottom - portalHeight);
    verticalClamped = true;
  }
  if (top < VIEWPORT_GUTTER) {
    top = VIEWPORT_GUTTER;
    verticalClamped = true;
  }

  let left = rect.left;
  let horizontalClamped = false;
  if (left + portalWidth > window.innerWidth - VIEWPORT_GUTTER) {
    left = window.innerWidth - portalWidth - VIEWPORT_GUTTER;
    horizontalClamped = true;
  }
  if (left < VIEWPORT_GUTTER) {
    left = VIEWPORT_GUTTER;
    horizontalClamped = true;
  }

  return {
    top,
    left,
    placement,
    flipped,
    horizontalClamped,
    verticalClamped,
  };
}

function CanvasViewportDismissal({ onClose }: { onClose: () => void }) {
  useOnViewportChange({
    onStart: useCallback(() => {
      portalLog("closing on viewport change");
      onClose();
    }, [onClose]),
  });
  return null;
}

export function FlyoutPortal({
  triggerRef,
  isOpen,
  onClose,
  children,
  className = "",
  surfaceClassName = "bg-surface-1 border border-border rounded-xl shadow-2xl p-3 max-h-[70vh] overflow-y-auto",
  minWidth = 260,
  preferredPlacement = "above",
  closeWhenTriggerLeavesViewport = false,
  closeOnCanvasViewportChange = true,
  onEscape,
  onPositionChange,
  portalHost,
  testId = "flyout-portal",
  zIndex,
}: FlyoutPortalProps) {
  const portalRef = useRef<HTMLDivElement>(null);
  const lastPositionRef = useRef<Position | null>(null);
  const [pos, setPos] = useState<Position | null>(null);

  const recalculate = useCallback(() => {
    if (!triggerRef.current || !portalRef.current) return;
    const triggerEl = triggerRef.current;
    const triggerRect = triggerEl.getBoundingClientRect();
    const hasMeasurableTrigger =
      triggerRect.width > 0 && triggerRect.height > 0;
    const triggerOutsideViewport =
      triggerRect.bottom <= 0 ||
      triggerRect.top >= window.innerHeight ||
      triggerRect.right <= 0 ||
      triggerRect.left >= window.innerWidth;
    if (
      closeWhenTriggerLeavesViewport &&
      hasMeasurableTrigger &&
      triggerOutsideViewport
    ) {
      onClose();
      return;
    }

    const portalEl = portalRef.current;
    const newPos = computePosition(
      triggerEl,
      portalEl,
      minWidth,
      preferredPlacement,
    );
    const current = lastPositionRef.current;
    if (
      current?.top === newPos.top &&
      current.left === newPos.left &&
      current.placement === newPos.placement &&
      current.flipped === newPos.flipped &&
      current.horizontalClamped === newPos.horizontalClamped &&
      current.verticalClamped === newPos.verticalClamped
    ) {
      return;
    }

    lastPositionRef.current = newPos;
    setPos(newPos);
  }, [
    closeWhenTriggerLeavesViewport,
    minWidth,
    onClose,
    preferredPlacement,
    triggerRef,
  ]);

  useEffect(() => {
    if (!isOpen) {
      lastPositionRef.current = null;
      setPos(null);
      return;
    }
    const raf = requestAnimationFrame(recalculate);
    const handleViewportMovement = () => recalculate();

    window.addEventListener("resize", handleViewportMovement);
    window.addEventListener("scroll", handleViewportMovement, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", handleViewportMovement);
      window.removeEventListener("scroll", handleViewportMovement, true);
    };
  }, [isOpen, recalculate]);

  useEffect(() => {
    if (!pos || !portalRef.current) return;
    onPositionChange?.({
      ...pos,
      width: portalRef.current.offsetWidth,
      height: portalRef.current.offsetHeight,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    });
  }, [onPositionChange, pos]);

  useClickOutside([portalRef, triggerRef], onClose, isOpen, onEscape);
  useBlockingOverlay(isOpen);

  if (!isOpen) return null;

  const fullscreenHost = document.fullscreenElement;
  const trigger = triggerRef.current;
  const target =
    portalHost ??
    (fullscreenHost instanceof Element &&
    trigger !== null &&
    fullscreenHost.contains(trigger)
      ? fullscreenHost
      : document.body);

  const portal = createPortal(
    <div
      ref={portalRef}
      className={`fixed ${zIndex === undefined ? "z-[150]" : ""} nopan nodrag nowheel ${className}`}
      style={{
        top: pos ? pos.top : -9999,
        left: pos ? pos.left : -9999,
        minWidth,
        // Only when a caller opts in, so canvas flyouts keep the z-[150] class
        // they and their tests already assert.
        ...(zIndex === undefined ? {} : { zIndex }),
        visibility: pos ? "visible" : "hidden",
      }}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      data-testid={testId}
    >
      <div className={surfaceClassName}>{children}</div>
    </div>,
    target,
  );

  return (
    <>
      {closeOnCanvasViewportChange ? (
        <CanvasViewportDismissal onClose={onClose} />
      ) : null}
      {portal}
    </>
  );
}
